import { Injectable } from "@angular/core";
import { Observable, ReplaySubject } from "rxjs";
import { map, take } from "rxjs/operators";
import { ContentType } from "../shared/contentTypes";
import { LocationDataModel, MenuSettingsDataModel2, MenuSettingsDMKey } from "../shared/models";
import { ordertypeSettings } from "../shared/models/ordertype.model";
import { OrdertypeViewModel, orderTypes, WebshopSettingsDataModel2, WebshopSettingsDMKey } from "../shared/models/webshop.model";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { IObjectObserver } from "./IObjectObserver";
import { CoreLogService } from "./log.service";
import { ObjectObserverFactory } from "./objectObserverFactory";

@Injectable({
  providedIn: "root",
})
export class WebshopOrdertypesService {
  public get ordertypes$(): Observable<OrdertypeViewModel[]> {
    return this._ordertypesSubject.asObservable();
  }

  public get supportedOrdertypes$(): Observable<OrdertypeViewModel[]> {
    return this._ordertypesSubject.asObservable().pipe(
      map(ordertypes => ordertypes.filter(ordertype => ordertype.supported)),
    );
  }

  private _ordertypes: OrdertypeViewModel[];
  private _ordertypesSubject: ReplaySubject<OrdertypeViewModel[]> = new ReplaySubject<OrdertypeViewModel[]>(1);

  private _webshopSettingsObsServ: IObjectObserver<WebshopSettingsDataModel2>;
  private _locationObsServ: IObjectObserver<LocationDataModel>;
  private _menuSettingsObsServ: IObjectObserver<MenuSettingsDataModel2>;

  constructor(
    private _coreLogService: CoreLogService,
    _objObsInjectHelper: CircularInjectionHelper,
  ) {
    const me = this;
    me._webshopSettingsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebshopSettingsDataModel2>(ContentType.WebshopSettings, _objObsInjectHelper);
    me._locationObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<LocationDataModel>(ContentType.Location, _objObsInjectHelper);
    me._menuSettingsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<MenuSettingsDataModel2>(ContentType.MenuSettings, _objObsInjectHelper);

    me._webshopSettingsObsServ.ObserveChanges.subscribe(async changes => {
      await this._updateOrdertypes();
    });

    this._updateOrdertypes();
  }

  public getOrdertypeByID(ordertypeID: orderTypes): Observable<OrdertypeViewModel> {
    return this._ordertypesSubject.asObservable().pipe(
      map(ordertypes => ordertypes.find(ordertype => ordertype.id === ordertypeID)),
      take(1)
    );
  }

  public getOrdertypeByID$(ordertypeID: orderTypes): Observable<OrdertypeViewModel> {
    return this._ordertypesSubject.asObservable().pipe(
      map(ordertypes => ordertypes.find(ordertype => ordertype.id === ordertypeID)),
    );
  }

  private async _updateOrdertypes() {
    const me = this;
    const webshopSettings = await this._webshopSettingsObsServ.GetObject(WebshopSettingsDMKey);
    const menuSettings = await this._menuSettingsObsServ.GetObject(MenuSettingsDMKey);
    if (menuSettings) {
      const allLocations = await me._locationObsServ.GetAllObjects();
      const locations = allLocations.filter(loc => menuSettings.locations.includes(loc.hyp_cus_nr));
      me._ordertypes = me._composeOrdertypes(webshopSettings, locations);
      me._ordertypesSubject.next(me._ordertypes);
    }
  }

  private _composeOrdertypes(webshopSettings: WebshopSettingsDataModel2, locations: LocationDataModel[]): OrdertypeViewModel[] {
    if (!webshopSettings || !webshopSettings.ordertypes || webshopSettings.ordertypes.length === 0) {
      this._coreLogService.error("WebshopOrdertypesService._composeOrdertypes", "No ordertypes found in webshop settings");
      return [];
    }

    const ordertypes = webshopSettings.ordertypes.map((ordertype) => {
      const ordertypeSetting = ordertypeSettings.find((os) => os.id === ordertype.id);
      if (!ordertypeSetting) {
        this._coreLogService.error("WebshopOrdertypesService._composeOrdertypes", `No ordertype setting found for ordertype id ${ordertype.id}`);
        return null;
      }
      const servicelocations = locations.filter(loc => loc[ordertypeSetting.locationServicePropertyName]);
      return new OrdertypeViewModel(ordertype, ordertypeSetting, servicelocations.map(location => location.hyp_cus_nr));
    }).filter(ordertype => ordertype !== null);

    if (ordertypes.length === 0) {
      this._coreLogService.error("WebshopOrdertypesService._composeOrdertypes", "No valid ordertypes composed");
    }

    return ordertypes;
  }
}
